import { useState, useRef, useEffect, useCallback } from "react"
import { Box, Container, IconButton, Typography } from "@mui/material"
import { Pause, Stop, Close, PlayArrow as PlayIcon, Refresh } from "@mui/icons-material"
import { useNavigate } from "react-router-dom"
import { useRemindMeContext } from "../../context/RemindMeContext";
import { useAlert } from "../../context/AlertContext";
import { useDeviceId } from "../../hooks/useDeviceId";
import { sendEmailVoice, transcribeAudio } from "../../utils/remindMeServices/VoiceToMail";
import { companyId } from "../../utils/remindMeServices/RemindMeAPIServices";
import CommonLoader from "../../components/remindMe/CommonLoader";
import MicWithAnimation from "../../components/remindMe/sendVoice/MicWithAnimation";
import PlayerSlider from "../../components/remindMe/sendVoice/PlayerSlider";
import ButtonStart from "../../components/remindMe/sendVoice/ButtonStart";
import BottomSendContainer from "../../components/remindMe/BottomSendContainer";
import NoEmailDialog from "../../components/remindMe/NoEmailDialog";
import InnerTopBar from "../../components/layout/InnerTopBar";
type EmailSendingTask = () => Promise<void>;


export default function VoiceRecorder() {
    const { selectedEmail } = useRemindMeContext();
    const [loading, setLoading] = useState(false)
    const [isRecording, setIsRecording] = useState(false)
    const [hasRecording, setHasRecording] = useState(false)
    const [isPlaying, setIsPlaying] = useState(false)
    const [isPaused, setIsPaused] = useState(false)
    const [recordingTime, setRecordingTime] = useState(0)
    const [playbackTime, setPlaybackTime] = useState(0)
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [duration, setDuration] = useState(0)
    const [audioURL, setAudioURL] = useState<string | null>(null)
    const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
    const [audioBase64, setAudioBase64] = useState<string | null>(null)
    const mediaRecorderRef = useRef<MediaRecorder | null>(null)
    const audioRef = useRef<HTMLAudioElement | null>(null)
    const chunksRef = useRef<BlobPart[]>([])
    const timerRef = useRef<number | null>(null)
    const startTimeRef = useRef<number>(0)
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const { deviceId } = useDeviceId()
    const emailQueue: EmailSendingTask[] = [];

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current)
            }
            if (audioURL) {
                URL.revokeObjectURL(audioURL)
            }
        }
    }, [audioURL])

    useEffect(() => {
        if (audioRef.current && hasRecording) {
            const audio = audioRef.current

            const setAudioDuration = () => {
                if (isFinite(audio.duration) && audio.duration > 0) {
                    setDuration(audio.duration)
                } else {
                    // Fallback: use recordingTime if available, or try to determine duration manually
                    setDuration(recordingTime)
                }
            }

            const handleTimeUpdate = () => {
                console.log("audio.currentTime", audio.currentTime)
                setPlaybackTime(audio.currentTime)
            }

            const handleEnded = () => {
                console.log("handleEnded")
                setIsPlaying(false)
                setPlaybackTime(0)
            }

            audio.addEventListener("loadedmetadata", setAudioDuration)
            audio.addEventListener("durationchange", setAudioDuration)
            audio.addEventListener("timeupdate", handleTimeUpdate)
            audio.addEventListener("ended", handleEnded)

            // Attempt to load audio metadata
            audio.load()

            return () => {
                audio.removeEventListener("loadedmetadata", setAudioDuration)
                audio.removeEventListener("durationchange", setAudioDuration)
                audio.removeEventListener("timeupdate", handleTimeUpdate)
                audio.removeEventListener("ended", handleEnded)
            }
        }
    }, [audioURL, hasRecording, recordingTime])

    useEffect(() => {
        if (isRecording) {
            setPlaybackTime(0)
        }
    }, [isRecording])
    useEffect(() => {
        if (!selectedEmail) {
            setIsDialogOpen(true);
        }
    }, [selectedEmail]);

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
            const mediaRecorder = new MediaRecorder(stream)
            mediaRecorderRef.current = mediaRecorder
            chunksRef.current = []

            mediaRecorder.ondataavailable = (e) => {
                chunksRef.current.push(e.data)
            }

            mediaRecorder.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: "audio/mp4" })
                blobToBase64(blob).then((base64) => {
                    setAudioBase64(base64)
                })
                const url = URL.createObjectURL(blob)
                setAudioURL(url)
                setHasRecording(true)
                setAudioBlob(blob);
                setDuration(recordingTime)
            }

            mediaRecorder.start()
            setIsRecording(true)
            setRecordingTime(0)
            timerRef.current = window.setInterval(() => {
                setRecordingTime((prevTime) => prevTime + 1)
            }, 1000)
        } catch (err) {
            console.error("Error accessing microphone:", err)
        }
    }
    const blobToBase64 = (blob: Blob): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onloadend = () => {
                if (typeof reader.result === "string") {
                    resolve(reader.result.split(",")[1]) // Remove the data URL prefix
                } else {
                    reject(new Error("Failed to convert Blob to base64"))
                }
            }
            reader.onerror = reject
            reader.readAsDataURL(blob)
        })
    }

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop()
            setIsRecording(false)
            setDuration(recordingTime)
            if (timerRef.current) {
                clearInterval(timerRef.current)
                timerRef.current = null
            }
        }
    }
    const refreshPlay = () => {
        if (audioRef.current) {
            audioRef.current.currentTime = 0
            setPlaybackTime(0);
            setIsPlaying(false);
            if (isPlaying) {
                audioRef.current.pause()
            }
        }
    }
    const pauseRecording = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current)
            timerRef.current = null
        }
        setIsPaused(true)
        mediaRecorderRef.current?.pause()
    }, [])

    const resumeRecording = useCallback(() => {
        if (!timerRef.current) {
            startTimeRef.current = Date.now() - recordingTime * 1000
            timerRef.current = window.setInterval(() => {
                setRecordingTime(Math.floor((Date.now() - startTimeRef.current) / 1000))
            }, 1000)
        }
        setIsPaused(false)
        mediaRecorderRef.current?.resume()
    }, [recordingTime])


    const togglePlayback = () => {
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause()
            } else {
                audioRef.current.play()
            }
            setIsPlaying(!isPlaying)
        }
    }

    const handleSliderChange = (_event: Event, newValue: number | number[]) => {
        const value = newValue as number
        setPlaybackTime(value)
        if (audioRef.current) {
            audioRef.current.currentTime = value
        }
    }

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60)
        const secs = Math.floor(seconds % 60)
        return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
    }

    const resetRecording = () => {
        setIsRecording(false)
        setHasRecording(false)
        setIsPlaying(false)
        setRecordingTime(0)
        setPlaybackTime(0)
        setDuration(0)
        if (audioURL) {
            URL.revokeObjectURL(audioURL)
            setAudioURL(null)
        }
        if (timerRef.current) {
            clearInterval(timerRef.current)
            timerRef.current = null
        }
    }
    const handleSend = (_email: string) => {
        sendAudio()
    }

    const sendAudio = async () => {
        setLoading(true);
        try {
            if (!selectedEmail || !audioBlob) {
                return;
            }
            await stopRecording();

            showAlert(`Success! Email Sent to ${selectedEmail}`, 'success');
            navigate("/home");
            const transcription = await transcribeAudio(audioBlob) || '';
            if (!audioBase64) {
                throw new Error('Failed to convert audio to base64');
            }
            if (!deviceId) {
                throw new Error('Device ID is missing.');
            }
            const companyIdNumber = companyId ? Number(companyId) : NaN;
            emailQueue.push(() => sendEmailVoice(
                selectedEmail,
                audioBase64,
                'voice',
                companyIdNumber,
                deviceId,
                transcription
            ));

            processEmailQueue();

        } catch (error) {
            console.error('Error processing audio:', error);
            showAlert('Oops! Sending email failed. Please check your data connection.', 'error');
        } finally {
            setAudioURL(null);
            setLoading(false);
        }
    };

    const processEmailQueue = async () => {
        if (emailQueue.length === 0) {
            return;
        }
        const sendEmailTask = emailQueue.shift();

        if (sendEmailTask) {
            try {
                await sendEmailTask();
            } catch (error) {
                console.error('Failed to send email:', error);
            } finally {
                processEmailQueue();
            }
        }
    };


    // const convertToBase64 = async (uri: string) => {
    //     try {
    //         const response = await fetch(uri)
    //         console.log("response", response)
    //         const blob = await response.blob()
    //         const reader = new FileReader()
    //         reader.onloadend = () => {
    //             if (typeof reader.result === "string") {
    //                 return reader.result;
    //             }
    //         }
    //         reader.readAsDataURL(blob)
    //         return null;
    //     } catch (error) {
    //         console.error('Error reading file:', error);
    //         return null;
    //     }
    // };


    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        navigate('/settings');
    };

    return (
        <Container
            maxWidth="sm"
            sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                flex: 1,
            }}
        >
            <InnerTopBar title="Send Voice" />

            {loading ?
                <CommonLoader isVisible={loading} /> :
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 4,
                        pt: 2
                    }}
                >
                    <MicWithAnimation isRecording={isRecording && !isPaused} />
                    <Box sx={{ width: "100%", px: 2 }}>
                        <Typography
                            variant="h4"
                            sx={{
                                fontFamily: "monospace",
                                fontWeight: "bold",
                                mb: 1,
                                textAlign: "center",
                            }}
                        >
                            {formatTime(recordingTime)}
                        </Typography>

                        {hasRecording && !isRecording && (
                            <PlayerSlider
                                value={playbackTime}
                                totalDuration={duration}
                                onChange={handleSliderChange}
                            />
                        )}
                    </Box>

                    <Box sx={{ display: "flex", width: "90%", justifyContent: "space-between", alignItems: "center" }}>
                        {!isRecording && !hasRecording ? (
                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>
                                <ButtonStart onClick={startRecording} size={48} sizeInnerCircle={20} />
                            </Box>
                        ) : (
                            <>
                                {isRecording ?
                                    isPaused ?
                                        <ButtonStart onClick={resumeRecording} size={48} sizeInnerCircle={20} />
                                        :
                                        <IconButton
                                            sx={{ border: 1, borderColor: "divider" }}
                                            onClick={pauseRecording}>
                                            <Pause sx={{ fontSize: 30 }} />
                                        </IconButton> :
                                    <IconButton
                                        sx={{ border: 1, borderColor: "divider" }}
                                        onClick={refreshPlay}
                                    >
                                        <Refresh sx={{ fontSize: 30 }} />
                                    </IconButton>

                                }
                                {isRecording ? (
                                    <IconButton
                                        sx={{
                                            border: 1,
                                            borderColor: "divider",
                                            bgcolor: "white",
                                            color: "error.main",
                                            "&:hover": {
                                                bgcolor: "error.dark",
                                            },
                                        }}
                                        onClick={stopRecording}
                                    >
                                        <Stop sx={{ fontSize: 30 }} />
                                    </IconButton>
                                ) : (
                                    <IconButton
                                        sx={{
                                            border: 1,
                                            borderColor: "divider",
                                            bgcolor: "white",
                                            color: "primary.main",
                                            "&:hover": {
                                                bgcolor: "primary.light",
                                            },
                                        }}
                                        onClick={togglePlayback}
                                    >
                                        {(isPaused || !isPlaying) ?
                                            <PlayIcon sx={{ fontSize: 30 }} /> :
                                            <Stop sx={{ fontSize: 30 }} />
                                        }
                                    </IconButton>
                                )}
                                <IconButton sx={{ border: 1, borderColor: "divider" }} onClick={resetRecording}>
                                    <Close sx={{ fontSize: 30 }} />
                                </IconButton>
                            </>
                        )}
                    </Box>

                </Box>}
            {/* Bottom Section */}
            <BottomSendContainer
                handleSend={handleSend}
                isSendEnabled={hasRecording}
            />
            {/* Dialog */}
            <NoEmailDialog isDialogOpen={isDialogOpen} handleCloseDialog={handleCloseDialog} />

            {audioURL && (
                <audio
                    ref={audioRef}
                    src={audioURL}
                    onEnded={() => {
                        setIsPlaying(false)
                        setPlaybackTime(0)
                    }}
                    hidden
                />
            )}
        </Container>
    )
}

